'use client'

import React, { useMemo, useRef, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { BaseLayoutContext, BaseLayoutSection } from '../../types/base-layout'
import { BasePhoto } from '../../types/base-content'
import { useSession } from '@/features/auth/context/session.context'
import { authFetchJson } from '@/shared/lib/auth-fetch'
import { UserRole } from '@/shared/constants/user-roles'
import { FileText, PlusIcon } from 'lucide-react'
import errorToast from '@/shared/lib/toast/error.toast'
import successToast from '@/shared/lib/toast/success.toast'
import AddBasePhotoModal from '../add-base-photo-modal'
import BasePhotoViewer from '../base-photo-viewer'
import VideoThumbnail from '@/features/photos/components/video-thumbnail'
import { PdfThumbnail } from '@/features/photos/components/pdf-thumbnail'
import VideoPlayer from '@/features/photos/components/video-player'
import PhotoDescriptionIndicator from '@/features/photos/components/photo-description-indicator'
import VoiceRecordingModal from '@/features/photos/components/voice-recording-modal'
import TextInputModal from '@/features/photos/components/text-input-modal'
import { updateBasePhoto, deleteBasePhoto } from '@/features/base/api/base-photos.api'

const dateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })

interface BasePhotosSectionProps {
  section: BaseLayoutSection
  context: BaseLayoutContext
}

const DEFAULT_PREVIEW_LIMIT = 6

function formatTakenDate(photo: BasePhoto): string | null {
  if (!photo.takenAt) {
    return null
  }
  const date = new Date(photo.takenAt)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return dateFormatter.format(date)
}

export function BasePhotosSection({ section, context }: BasePhotosSectionProps) {
  const { session } = useSession()
  const componentName = section.title ?? 'Base Photos'
  const photos = useMemo(
    () => (context.photos ?? []).filter((p) => (p.componentName ?? 'Base Photos') === componentName),
    [context.photos, componentName],
  )
  const limitOption = section.options?.['limit']
  const limit = typeof limitOption === 'number' && limitOption > 0 ? Math.floor(limitOption) : DEFAULT_PREVIEW_LIMIT
  const visiblePhotos = useMemo(() => (limit ? photos.slice(0, limit) : photos), [photos, limit])

  const canManagePhotos = Boolean(context.permissions?.canManagePhotos)
  /** Volunteer and TeamLeader may only edit/delete photos they uploaded. Admin and BaseLeader may edit any. */
  const canEditPhoto = (photo: BasePhoto) => {
    if (!canManagePhotos) return false
    if (session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER) return true
    if (session?.role === UserRole.VOLUNTEER || session?.role === UserRole.TEAM_LEADER) {
      const uploaderId = photo.uploadedByUser?.id ?? photo.uploadedByUserId
      return uploaderId != null && uploaderId === session?.sub
    }
    return false
  }
  const canDownload = Boolean(context.permissions?.canDownloadPhotos)
  const regionId = context.meta?.regionId ?? null

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [preSelectedFiles, setPreSelectedFiles] = useState<File[]>([])
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [previewPhoto, setPreviewPhoto] = useState<BasePhoto | null>(null)
  const [previewVideo, setPreviewVideo] = useState<BasePhoto | null>(null)
  const [pendingViewerDescription, setPendingViewerDescription] = useState<string | undefined>(undefined)
  const [isViewerVoiceOpen, setIsViewerVoiceOpen] = useState(false)
  const [isViewerTextOpen, setIsViewerTextOpen] = useState(false)
  const [isViewerTranscribing, setIsViewerTranscribing] = useState(false)

  const refreshPhotos = context.actions?.refreshPhotos

  const handleUploadSuccess = async () => {
    if (typeof refreshPhotos === 'function') {
      await refreshPhotos()
    }
    setPreSelectedFiles([])
  }

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files?.length) {
      setPreSelectedFiles(Array.from(files))
      setIsUploadModalOpen(true)
    }
    e.target.value = ''
  }

  const handleDelete = async (photoId: number) => {
    if (!regionId) return
    const confirmed = window.confirm('Delete this photo/video permanently?')
    if (!confirmed) return

    try {
      const result = await deleteBasePhoto(photoId)
      if (result.success) {
        successToast('Deleted')
        setPreviewPhoto(null)
        setPreviewVideo(null)
        if (typeof refreshPhotos === 'function') {
          await refreshPhotos()
        }
      } else {
        errorToast('message' in result ? result.message : 'Failed to delete')
      }
    } catch (error) {
      console.error('Failed to delete photo', error)
      errorToast(error instanceof Error ? error.message : 'Failed to delete photo')
    }
  }

  const transcribeAudio = async (audioBlob: Blob) => {
    const f = new File([audioBlob], 'recording.webm', { type: audioBlob.type || 'audio/webm' })
    const formData = new FormData()
    formData.append('audio', f)
    const data = await authFetchJson<any>('/ai/transcribe', { method: 'POST', body: formData })
    if (!data.success) return { success: false as const, message: data.message || 'Transcription failed' }
    if (data.data?.transcription != null) return { success: true as const, text: String(data.data.transcription) }
    return { success: false as const, message: 'No transcription' }
  }

  const handleViewerVoiceSave = async (audioBlob: Blob) => {
    setIsViewerVoiceOpen(false)
    const photo = previewPhoto ?? previewVideo
    if (!photo || !audioBlob || audioBlob.size < 100) return
    setIsViewerTranscribing(true)
    try {
      const out = await transcribeAudio(audioBlob)
      if (out.success) {
        const existing = (photo.description ?? '').trim()
        const newDesc = existing ? `${existing} ${out.text}` : out.text
        setPendingViewerDescription(newDesc)
        const r = await updateBasePhoto(photo.id, { description: newDesc })
        if (r.success) {
          setPendingViewerDescription(undefined)
          setPreviewPhoto((p) => (p?.id === photo.id ? { ...p, description: newDesc } : p))
          setPreviewVideo((v) => (v?.id === photo.id ? { ...v, description: newDesc } : v))
          if (typeof refreshPhotos === 'function') await refreshPhotos()
        } else {
          errorToast('message' in r ? r.message : 'Failed to save')
        }
      } else {
        errorToast(out.message)
      }
    } catch (e) {
      errorToast(e instanceof Error ? e.message : 'Transcription error')
    } finally {
      setIsViewerTranscribing(false)
    }
  }

  const handleViewerTextSave = async (text: string) => {
    setIsViewerTextOpen(false)
    const photo = previewPhoto ?? previewVideo
    if (!photo) return
    setPendingViewerDescription(text)
    try {
      const r = await updateBasePhoto(photo.id, { description: text })
      if (r.success) {
        setPendingViewerDescription(undefined)
        setPreviewPhoto((p) => (p?.id === photo.id ? { ...p, description: text } : p))
        setPreviewVideo((v) => (v?.id === photo.id ? { ...v, description: text } : v))
        if (typeof refreshPhotos === 'function') await refreshPhotos()
      } else {
        errorToast('message' in r ? r.message : 'Failed to save')
      }
    } catch {
      errorToast('Failed to save description')
    }
  }

  const handleItemClick = (photo: BasePhoto) => {
    const isVideo = (photo.mediaType ?? 'image') === 'video'
    if (isVideo) {
      setPreviewVideo(photo)
      setPreviewPhoto(null)
    } else {
      setPreviewPhoto(photo)
      setPreviewVideo(null)
    }
    setPendingViewerDescription(undefined)
  }

  const closePreview = () => {
    setPreviewPhoto(null)
    setPreviewVideo(null)
    setPendingViewerDescription(undefined)
    setIsViewerVoiceOpen(false)
    setIsViewerTextOpen(false)
    setIsViewerTranscribing(false)
  }

  const displayPhoto = previewPhoto ?? previewVideo
  const isVideo = displayPhoto ? (displayPhoto.mediaType ?? 'image') === 'video' : false
  const isDoc = displayPhoto ? (displayPhoto.mediaType ?? 'image') === 'document' : false

  return (
    <Card key={section.id}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <div>
          <CardTitle>{section.title ?? 'Base Photos'}</CardTitle>
        </div>
        {canManagePhotos && (
          <Button
            size="sm"
            onClick={handleUploadButtonClick}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            <PlusIcon className="w-3 h-3" /> Upload
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-4">
          {visiblePhotos.map((photo) => {
            const mediaType = photo.mediaType ?? 'image'
            const isVideoItem = mediaType === 'video'
            const isDocItem = mediaType === 'document'
            return (
              <div key={photo.id} className="rounded-md border border-border bg-card pb-3">
                <div
                  role="button"
                  tabIndex={0}
                  className="relative block cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary"
                  onClick={() => handleItemClick(photo)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleItemClick(photo)
                    }
                  }}
                >
                  {isVideoItem ? (
                    <VideoThumbnail
                      photo={photo as unknown as import('@/features/photos/api/photos.api').Photo}
                      onClick={() => handleItemClick(photo)}
                      showDuration
                      showVideoIcon
                    />
                  ) : isDocItem ? (
                    <>
                      <PdfThumbnail
                        downloadApiPath={`/api/base-photos/${photo.id}/download`}
                        className="object-cover rounded rounded-t-md"
                        style={{ height: '200px', width: 'auto' }}
                        alt={photo.description || `PDF ${photo.id}`}
                      >
                        <div className="flex flex-col items-center justify-center rounded-t-md p-4 h-[200px] min-w-[140px] bg-muted">
                          <FileText className="w-12 h-12 text-muted-foreground mb-2" />
                          <span className="text-xs">PDF</span>
                        </div>
                      </PdfThumbnail>
                      <PhotoDescriptionIndicator hasDescription={!!photo.description} />
                    </>
                  ) : (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={photo.thumbnailUrl || photo.imageUrl}
                        alt={photo.description || 'Base photo'}
                        className="h-[200px] w-auto object-cover rounded-t-md"
                      />
                      <PhotoDescriptionIndicator hasDescription={!!photo.description} />
                    </>
                  )}
                </div>
                <div className="space-y-1 px-3">
                  {photo.description && (
                    <p className="pt-2 text-sm text-muted-foreground whitespace-pre-wrap line-clamp-3">{photo.description}</p>
                  )}
                  {formatTakenDate(photo) && (
                    <p className="text-xs text-muted-foreground">Taken {formatTakenDate(photo)}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {visiblePhotos.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {canManagePhotos ? 'No photos yet. Click Upload to add.' : 'No photos available.'}
          </p>
        )}
      </CardContent>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.pdf,application/pdf"
        multiple
        onChange={handleFileSelection}
        className="hidden"
      />

      <AddBasePhotoModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false)
          setPreSelectedFiles([])
        }}
        regionId={Number(regionId ?? 0)}
        onUploadSuccess={handleUploadSuccess}
        initialFiles={preSelectedFiles}
        componentName={componentName}
      />

      {/* Photo/PDF preview - same component as timeline */}
      {displayPhoto && !isVideo && (
        <BasePhotoViewer
          photo={displayPhoto}
          isOpen
          onClose={closePreview}
          description={displayPhoto.description}
          pendingDescription={pendingViewerDescription}
          isTranscribing={isViewerTranscribing}
          canEdit={canEditPhoto(displayPhoto)}
          canDownload={canDownload}
          onDelete={canEditPhoto(displayPhoto) ? handleDelete : undefined}
          onVoiceClick={canEditPhoto(displayPhoto) ? () => setIsViewerVoiceOpen(true) : undefined}
          onKeyboardClick={canEditPhoto(displayPhoto) ? () => setIsViewerTextOpen(true) : undefined}
          allPhotos={photos.filter((p) => (p.mediaType ?? 'image') !== 'video')}
          currentIndex={photos
            .filter((p) => (p.mediaType ?? 'image') !== 'video')
            .findIndex((p) => p.id === displayPhoto.id)}
          onSelectIndex={(i) => {
            const list = photos.filter((p) => (p.mediaType ?? 'image') !== 'video')
            if (list[i]) {
              setPreviewPhoto(list[i])
              setPreviewVideo(null)
            }
          }}
          hasConfidentialTag={(displayPhoto.tags ?? []).includes('Confidential')}
          onToggleConfidential={
            canEditPhoto(displayPhoto)
              ? async () => {
                  const current = displayPhoto.tags ?? []
                  const hasTag = current.includes('Confidential')
                  const next = hasTag
                    ? current.filter((t) => t !== 'Confidential')
                    : [...current, 'Confidential']
                  const r = await updateBasePhoto(displayPhoto.id, { tags: next })
                  if (r.success) {
                    setPreviewPhoto((p) => (p ? { ...p, tags: next } : null))
                    if (typeof refreshPhotos === 'function') await refreshPhotos()
                  } else {
                    errorToast(r.message ?? 'Failed to update tag')
                  }
                }
              : undefined
          }
        />
      )}

      {(previewPhoto || previewVideo) && isViewerVoiceOpen && (
        <VoiceRecordingModal
          isOpen
          onClose={() => setIsViewerVoiceOpen(false)}
          onSave={handleViewerVoiceSave}
          autoStart
          imageUrl={((previewPhoto ?? previewVideo)!.mediaType ?? 'image') === 'video'
            ? (previewPhoto ?? previewVideo)!.thumbnailUrl
            : (previewPhoto ?? previewVideo)!.imageUrl}
        />
      )}
      {displayPhoto && isViewerTextOpen && (
        <TextInputModal
          isOpen
          onClose={() => setIsViewerTextOpen(false)}
          onSave={handleViewerTextSave}
          initialValue={pendingViewerDescription ?? displayPhoto.description ?? ''}
          imageUrl={(displayPhoto.mediaType ?? 'image') === 'video' ? displayPhoto.thumbnailUrl! : displayPhoto.imageUrl}
        />
      )}

      {/* Video preview */}
      {previewVideo && (previewVideo.mediaType ?? 'image') === 'video' && (
        <VideoPlayer
          photo={previewVideo as unknown as import('@/features/photos/api/photos.api').Photo}
          isOpen
          onClose={closePreview}
          onDelete={() => {
            if (typeof refreshPhotos === 'function') refreshPhotos()
            setPreviewVideo(null)
          }}
          canDelete={canEditPhoto(previewVideo)}
          canDownload={canDownload}
          downloadApiPath={`/api/base-photos/${previewVideo.id}/download`}
          deletePhotoFn={deleteBasePhoto}
          onEditDescriptionVoice={canEditPhoto(previewVideo) ? () => setIsViewerVoiceOpen(true) : undefined}
          onEditDescriptionText={canEditPhoto(previewVideo) ? () => setIsViewerTextOpen(true) : undefined}
        />
      )}
    </Card>
  )
}
